import { useState } from "react";
import moment from "moment";
import TraingleIcon from "../../../svg/triangle";
import { Map, Marker } from "pigeon-maps";

const Message = ({message, me, showAvatarAndTail = true, replyPreview = null}) => {
    const senderPicture = message?.sender?.picture;
    const senderName = message?.sender?.name;
    const isFromAdmin = message?.metadata?.sentByAdmin;
    const adminName = message?.metadata?.adminName;
    const displayName = me ? (isFromAdmin ? (adminName || senderName || 'مدير') : 'الذكاء الاصطناعي') : null;
    const avatarUrl = me ? (senderPicture || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(displayName || 'AI') + '&background=005C4B&color=fff') : senderPicture;
    const content = message?.message || message?.content || "";
    const toolCalls = message?.toolCalls || [];
    const toolImages = toolCalls.flatMap((tc) => {
      if (tc?.name !== "send_images") return [];
      try {
        const args = typeof tc.arguments === "string" ? JSON.parse(tc.arguments) : tc.arguments;
        return Array.isArray(args?.images) ? args.images : [];
      } catch (_e) {
        return [];
      }
    });
    const toolImageUrls = toolImages.map((img) => img?.url).filter(Boolean);
    const bracketUrlMatch = content.match(/\[Image URL:\s*([^\]]+)\]/i);
    const inlineMediaUrl = bracketUrlMatch?.[1] || "";
    const derivedTypeFromUrl = inlineMediaUrl ? "image" : "";
    const derivedTypeFromTool = toolImageUrls.length > 0 ? "image" : "";
    const rawType = (message?.metadata?.messageType || "").toLowerCase();
    const baseType = rawType === "text" ? "" : rawType;
    const extraType = (message?.metadata?.additionalData?.mediaType || "").toLowerCase();
    const mimeType = (message?.metadata?.mimeType || "").toLowerCase();
    const typeFromMime = mimeType.startsWith("image/")
      ? "image"
      : mimeType.startsWith("video/")
        ? "video"
        : mimeType.startsWith("audio/")
          ? "audio"
          : "";
    const placeholderTypeMatch = content.trim().match(/^\[(image|video|audio|voice|document|sticker)\]$/i);
    const placeholderType = placeholderTypeMatch ? placeholderTypeMatch[1].toLowerCase() : "";
    const normalizedType = (baseType || extraType || placeholderType || derivedTypeFromTool || derivedTypeFromUrl || typeFromMime || "text")
      .replace("photo", "image")
      .replace("animation", "video")
      .replace("video_note", "video");
    const messageType = normalizedType;
    const mediaUrl =
      message?.metadata?.mediaUrl ||
      message?.metadata?.imageUrl ||
      message?.metadata?.additionalData?.mediaUrl ||
      message?.metadata?.additionalData?.imageUrl ||
      inlineMediaUrl ||
      "";
    const fileName = message?.metadata?.fileName || "";
    const fallbackLocation = message?.metadata?.additionalData?.location || null;
    const location = message?.metadata?.location || fallbackLocation;
    const contacts = Array.isArray(message?.metadata?.contacts) ? message.metadata.contacts : [];
    const fallbackContact = message?.metadata?.additionalData?.contact;
    const effectiveContacts = contacts.length > 0
      ? contacts
      : (fallbackContact ? [{
          name: `${fallbackContact.first_name || ""} ${fallbackContact.last_name || ""}`.trim(),
          phone: fallbackContact.phone_number || "",
          email: ""
        }] : []);
    const isMediaType = ["image", "video", "audio", "voice", "document", "sticker"].includes(messageType);
    const showMedia = isMediaType && (!!mediaUrl || toolImageUrls.length > 0);
    const isMediaOnlyPlaceholder = /^\[(image|video|audio|voice|document|sticker)\]$/i.test(content.trim());
    const isImageUrlPlaceholder = /\[Image URL:\s*[^\]]+\]/i.test(content);
    const showText = messageType === "text"
      ? !!content
      : (!!content && !isMediaOnlyPlaceholder && !isImageUrlPlaceholder && toolImageUrls.length === 0);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [imagePreviewSrc, setImagePreviewSrc] = useState("");
    const locationLat = Number(location?.latitude);
    const locationLng = Number(location?.longitude);
    const hasLocationCoords = Number.isFinite(locationLat) && Number.isFinite(locationLng);
    const deliveryStatus = (message?.metadata?.deliveryStatus || "").toLowerCase();
    const readIndicator = !me ? "" : (
      ["read", "seen"].includes(deliveryStatus)
        ? "✓✓"
        : ["delivered"].includes(deliveryStatus)
          ? "✓✓"
          : ["sent", "queued", "pending"].includes(deliveryStatus)
            ? "✓"
            : ["failed"].includes(deliveryStatus)
              ? "!"
              : ""
    );

    return (
        <div
            className={`w-full flex mt-2 gap-2 max-w-[100%] ${
            me ? "ms-auto justify-start flex-row-reverse" : ""
            }`}
        >
            {/*Sender avatar - show only on last message of group; placeholder for alignment when hidden */}
            {showAvatarAndTail ? (
              (me ? avatarUrl : senderPicture) && (
                <div className="shrink-0 self-end flex flex-col items-center gap-0.5">
                    <img
                        src={me ? avatarUrl : senderPicture}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                    />
                    {me && displayName && (
                        <span className="text-[10px] text-dark_text_2 truncate max-w-[3rem]">{displayName}</span>
                    )}
                </div>
              )
            ) : (
              <div className="w-8 shrink-0" aria-hidden />
            )}
            {/*Message Container*/}
            <div className="relative w-fit max-w-[70%]">
            <div
                className={`relative dark:text-dark_text_1 p-2 rounded-lg
            ${me ? "bg-green_3" : "dark:bg-dark_bg_2"}
            `}
            >
                {/*Reply preview*/}
                {replyPreview && (
                  <div className="mb-2 rounded-lg bg-dark_bg_1/30 px-2 py-1 text-xs text-dark_text_2 border border-dark_border_1">
                    رد على: {replyPreview}
                  </div>
                )}
                {/*Media / Special content*/}
                {showMedia && (messageType === "image" || messageType === "sticker") && toolImageUrls.length > 0 && (
                  <div className="mb-2 grid gap-2">
                    {toolImages.map((img, idx) => (
                      <div key={`${img.url || "img"}-${idx}`} className="rounded-lg overflow-hidden bg-dark_bg_1/20">
                        <img
                          src={img.url}
                          alt=""
                          className="max-h-80 w-full object-contain cursor-pointer"
                          onClick={() => setImagePreviewSrc(img.url)}
                          title="عرض الصورة"
                        />
                        {img.caption && (
                          <div className="p-2 text-xs text-dark_text_2">{img.caption}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {showMedia && (messageType === "image" || messageType === "sticker") && toolImageUrls.length === 0 && (
                  <div className="mb-2 rounded-lg overflow-hidden bg-dark_bg_1/20">
                    <img
                      src={mediaUrl}
                      alt=""
                      className="max-h-80 w-full object-contain cursor-pointer"
                      onClick={() => setImagePreviewSrc(mediaUrl)}
                      title="عرض الصورة"
                    />
                  </div>
                )}
                {showMedia && messageType === "video" && (
                  <div className="mb-2 rounded-lg overflow-hidden bg-dark_bg_1/20">
                    <video src={mediaUrl} controls className="max-h-80 w-full" />
                  </div>
                )}
                {showMedia && (messageType === "audio" || messageType === "voice") && (
                  <div className="mb-2 rounded-lg bg-dark_bg_1/20 p-2">
                    <audio src={mediaUrl} controls className="w-full" />
                  </div>
                )}
                {showMedia && messageType === "document" && (
                  <div className="mb-2 rounded-lg bg-dark_bg_1/20 p-2">
                    <a href={mediaUrl} target="_blank" rel="noreferrer" className="text-sm underline">
                      {fileName || "فتح المستند"}
                    </a>
                  </div>
                )}
                {messageType === "location" && location && (
                  <div
                    className="mb-2 rounded-lg bg-dark_bg_1/20 p-2 text-sm cursor-pointer hover:bg-dark_bg_1/30 transition"
                    role="button"
                    tabIndex={0}
                    onClick={() => hasLocationCoords && setShowLocationModal(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (hasLocationCoords) setShowLocationModal(true);
                      }
                    }}
                    title="عرض الموقع على الخريطة"
                  >
                    <div>{location.name || "موقع"}</div>
                    <div className="text-xs text-dark_text_2">
                      {location.latitude}, {location.longitude}
                    </div>
                    {location.address && <div className="text-xs text-dark_text_2">{location.address}</div>}
                  </div>
                )}
                {messageType === "contact" && effectiveContacts.length > 0 && (
                  <div className="mb-2 rounded-lg bg-dark_bg_1/20 p-2 text-sm">
                    {effectiveContacts.map((c, idx) => (
                      <div key={`${c.phone || c.name || "contact"}-${idx}`} className="mb-1">
                        <div>{c.name || "جهة اتصال"}</div>
                        {c.phone && <div className="text-xs text-dark_text_2">{c.phone}</div>}
                        {c.email && <div className="text-xs text-dark_text_2">{c.email}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {messageType === "interactive" && (
                  <div className="mb-2 rounded-lg bg-dark_bg_1/20 p-2 text-sm">
                    رسالة تفاعلية
                  </div>
                )}
                {/*Message text*/}
                {showText && (
                  <p className="text-sm pb-4 pe-8 break-words whitespace-pre-wrap">
                    {content}
                  </p>
                )}
                {/*Message Date*/}
                <span className="absolute end-1.5 bottom-1.5 text-xs text-dark_text_5 leading-none z-10 flex items-center gap-1">
                  <span>{moment(message.createdAt).format("HH:mm")}</span>
                  {readIndicator && <span className="text-[11px]">{readIndicator}</span>}
                </span>
                {/*Traingle - show only on last message of group */}
                {showAvatarAndTail && (!me ? (
                <span>
                     <TraingleIcon className="dark:fill-dark_bg_2 rotate-[240deg] absolute bottom-[-5px] -start-1.5" />
                </span>
                ) : (
                <span>
                <TraingleIcon className="fill-green_3 rotate-[240deg] absolute bottom-[-5px] -end-2 z-0" />
                </span>
                ))}
            </div>
            </div>
            {showLocationModal && hasLocationCoords && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                <div className="w-full max-w-2xl dark:bg-dark_bg_2 border border-dark_border_1 rounded-xl overflow-hidden">
                  <div className="p-3 border-b border-dark_border_1 flex items-center justify-between">
                    <h3 className="text-dark_text_1 font-medium">الموقع</h3>
                    <button
                      type="button"
                      className="w-8 h-8 rounded-lg hover:bg-dark_hover_1 text-dark_text_2"
                      onClick={() => setShowLocationModal(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="p-3">
                    <div className="h-[360px] w-full rounded-lg overflow-hidden border border-dark_border_1">
                      <Map
                        center={[locationLat, locationLng]}
                        zoom={14}
                        onBoundsChanged={() => {}}
                      >
                        <Marker anchor={[locationLat, locationLng]} width={36} />
                      </Map>
                    </div>
                    <div className="mt-2 text-xs text-dark_text_2">
                      {location.latitude}, {location.longitude}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {imagePreviewSrc && (
              <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
                <div className="w-full max-w-3xl dark:bg-dark_bg_2 border border-dark_border_1 rounded-xl overflow-hidden">
                  <div className="p-3 border-b border-dark_border_1 flex items-center justify-between">
                    <h3 className="text-dark_text_1 font-medium">الصورة</h3>
                    <button
                      type="button"
                      className="w-8 h-8 rounded-lg hover:bg-dark_hover_1 text-dark_text_2"
                      onClick={() => setImagePreviewSrc("")}
                    >
                      ×
                    </button>
                  </div>
                  <div className="p-3">
                    <img src={imagePreviewSrc} alt="" className="max-h-[70vh] w-full object-contain" />
                  </div>
                </div>
              </div>
            )}
        </div>
    );
}

export default Message;
